import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'Services', href: '/services/carpet' },
  { label: 'About', href: '/about' },
  { label: 'Locations', href: '/location/freehold-nj' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToContact = () => {
    setMenuOpen(false);
    const el = document.querySelector('#contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const isActive = (href: string) => location.pathname === href;

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? 'bg-white border-b border-cream-dark shadow-sm' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0">
            <img
              src="https://e2bzbtvuvmf.exactdn.com/wp-content/uploads/2022/10/logo.png"
              alt="Value Carpet Inc"
              className="h-11 w-auto object-contain"
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.label}
                to={link.href}
                className={`text-sm font-medium tracking-wide transition-all duration-200 whitespace-nowrap relative group ${
                  scrolled
                    ? isActive(link.href) ? 'text-gold' : 'text-charcoal hover:text-gold'
                    : isActive(link.href) ? 'text-gold' : 'text-white/90 hover:text-gold'
                }`}
              >
                {link.label}
                <span
                  className={`absolute -bottom-0.5 left-0 h-px bg-gold transition-all duration-200 ${
                    isActive(link.href) ? 'w-full' : 'w-0 group-hover:w-full'
                  }`}
                />
              </Link>
            ))}
          </nav>

          {/* Desktop CTAs */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href="tel:1-866-732-2966"
              className={`text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-1.5 ${
                scrolled ? 'text-charcoal hover:text-gold' : 'text-white/90 hover:text-gold'
              }`}
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-phone-fill text-gold"></i>
              </span>
              1-866-732-2966
            </a>
            <button
              onClick={scrollToContact}
              className="bg-gold text-charcoal text-sm font-semibold px-5 py-2.5 rounded-md hover:bg-gold-light transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Request Consultation
            </button>
          </div>

          {/* Mobile Hamburger */}
          <button
            className="lg:hidden w-8 h-8 flex flex-col justify-center gap-1.5 cursor-pointer"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <span className={`block h-px transition-all duration-300 ${scrolled || menuOpen ? 'bg-charcoal' : 'bg-white'} ${menuOpen ? 'rotate-45 translate-y-[7px]' : ''}`} />
            <span className={`block h-px transition-all duration-300 ${scrolled || menuOpen ? 'bg-charcoal' : 'bg-white'} ${menuOpen ? 'opacity-0 w-0' : 'w-full'}`} />
            <span className={`block h-px transition-all duration-300 ${scrolled || menuOpen ? 'bg-charcoal' : 'bg-white'} ${menuOpen ? '-rotate-45 -translate-y-[7px]' : ''}`} />
          </button>
        </div>

        {/* Mobile sticky call bar */}
        <div className="lg:hidden bg-gold px-4 py-2 flex items-center justify-between">
          <span className="text-charcoal text-xs font-semibold">Premium Flooring • NJ Specialist</span>
          <a
            href="tel:1-866-732-2966"
            className="text-charcoal text-xs font-bold whitespace-nowrap flex items-center gap-1.5"
          >
            <i className="ri-phone-fill text-sm"></i> Call Now
          </a>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 transition-all duration-400 lg:hidden ${
          menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        style={{ background: '#1A1A1A', paddingTop: '88px' }}
      >
        <nav className="flex flex-col p-8 gap-6">
          {navLinks.map((link, i) => (
            <Link
              key={link.label}
              to={link.href}
              onClick={() => setMenuOpen(false)}
              className={`text-2xl font-serif hover:text-gold transition-colors ${
                isActive(link.href) ? 'text-gold' : 'text-white'
              }`}
              style={{ transitionDelay: menuOpen ? `${i * 50}ms` : '0ms' }}
            >
              {link.label}
            </Link>
          ))}
          <div className="pt-6 border-t border-white/10 flex flex-col gap-3">
            <a href="tel:1-866-732-2966" className="btn-primary text-center text-base">
              <i className="ri-phone-fill mr-2"></i>
              1-866-732-2966
            </a>
            <button
              onClick={scrollToContact}
              className="border border-gold/50 text-gold px-8 py-3.5 rounded-md font-medium cursor-pointer whitespace-nowrap hover:border-gold transition-colors"
            >
              Request Free Consultation
            </button>
          </div>
        </nav>
      </div>
    </>
  );
}
